import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { ArrowUpDown, RefreshCw } from "lucide-react";

// Minimalistic EUR ↔ Crypto converter. Uses the backend /api/crypto/prices
// endpoint (Coinbase + CoinGecko fallback, 5 min cache) so we do not need any
// third-party account or extra API key.
const COIN_META = [
  { key: "bitcoin", symbol: "BTC", name: "Bitcoin" },
  { key: "ethereum", symbol: "ETH", name: "Ethereum" },
  { key: "solana", symbol: "SOL", name: "Solana" },
  { key: "ripple", symbol: "XRP", name: "XRP" },
];

const fmtEur = (n) =>
  `€${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtCoin = (n, decimals = 8) => {
  const val = Number(n || 0);
  return val.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  });
};

export default function CryptoConverter() {
  const { t } = useLang();
  const [prices, setPrices] = useState(null);
  const [coin, setCoin] = useState("bitcoin");
  const [amount, setAmount] = useState("1000");
  const [direction, setDirection] = useState("eur_to_crypto"); // or crypto_to_eur
  const [loading, setLoading] = useState(false);

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crypto/prices");
      setPrices(data || {});
    } catch {
      /* silent — backend also has fallbacks */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
  }, []);

  const rate = prices?.[coin]?.eur || 0;
  const meta = COIN_META.find((c) => c.key === coin) || COIN_META[0];

  const output = useMemo(() => {
    const n = parseFloat(amount);
    if (!rate || Number.isNaN(n) || n <= 0) return "";
    if (direction === "eur_to_crypto") return fmtCoin(n / rate);
    return fmtEur(n * rate);
  }, [amount, rate, direction]);

  const flip = () => {
    setDirection((d) => (d === "eur_to_crypto" ? "crypto_to_eur" : "eur_to_crypto"));
    setAmount("");
  };

  const isEurInput = direction === "eur_to_crypto";

  return (
    <section
      data-testid="crypto-converter"
      className="border-t border-white/10 py-16 sm:py-24"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="mb-8 sm:mb-10 text-center sm:text-left">
          <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-3">
            {t("conv.tag")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-3">
            {t("conv.title")}
          </h2>
          <p className="text-white/60 text-sm">{t("conv.subtitle")}</p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 p-5 sm:p-6">
          {/* Coin selector row */}
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex flex-wrap gap-2">
              {COIN_META.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`conv-coin-${c.symbol.toLowerCase()}`}
                  onClick={() => setCoin(c.key)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
                    coin === c.key
                      ? "bg-[#FFD700] text-black border-[#FFD700]"
                      : "border-white/20 text-white/60 hover:text-white hover:border-white/40"
                  }`}
                >
                  {c.symbol}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={fetchPrices}
              disabled={loading}
              data-testid="conv-refresh-btn"
              className="text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/80 flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              {rate ? `1 ${meta.symbol} = ${fmtEur(rate)}` : t("conv.loading")}
            </button>
          </div>

          {/* Input row */}
          <div className="grid sm:grid-cols-[1fr_auto_1fr] items-end gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-2">
                {isEurInput ? t("conv.you.eur") : `${t("conv.you.crypto")} (${meta.symbol})`}
              </label>
              <div className="flex items-center gap-2 bg-black border border-white/20 focus-within:border-white px-3 py-3">
                <span className="text-[#FFD700] font-mono text-lg">
                  {isEurInput ? "€" : meta.symbol}
                </span>
                <input
                  data-testid="conv-amount-input"
                  type="number"
                  inputMode="decimal"
                  step="0.00000001"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={isEurInput ? "1000.00" : "0.01"}
                  className="flex-1 bg-transparent outline-none font-mono text-lg min-w-0"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={flip}
              data-testid="conv-flip-btn"
              className="w-10 h-10 border border-white/20 hover:border-[#FFD700] hover:text-[#FFD700] transition-colors flex items-center justify-center self-end mb-1 sm:mb-1"
              title={t("conv.flip")}
              aria-label={t("conv.flip")}
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-white/50 block mb-2">
                {isEurInput ? `${t("conv.get.crypto")} (${meta.symbol})` : t("conv.get.eur")}
              </label>
              <div
                data-testid="conv-output"
                className="bg-black border border-white/10 px-3 py-3 font-mono text-lg text-[#10B981] truncate min-h-[52px] flex items-center"
              >
                {output || <span className="text-white/30">—</span>}
              </div>
            </div>
          </div>

          <p className="mt-5 text-[10px] font-mono uppercase tracking-widest text-white/30">
            {t("conv.footnote")}
          </p>
        </div>
      </div>
    </section>
  );
}
